import { useCallback, useMemo } from "react";

import toast from "react-hot-toast";
import { useParams } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import type {
  ChallengeDetail,
  ChallengeSettingsUpdate,
  UseChallengeQueryReturn,
} from "../types";

type UseChallengeQueryOptions = { challengeId?: string };

// Settings are shown as changed straight away and saved one after another, so
// a toggle or list built from the shown challenge always includes earlier
// changes, and an earlier save's response never undoes a later change.
const saveQueues = new Map<string, Promise<void>>();
const pendingSaves = new Map<string, number>();

const applyUpdate = (
  challenge: ChallengeDetail,
  update: ChallengeSettingsUpdate,
): ChallengeDetail => {
  const next = { ...challenge, ...update } as ChallengeDetail;
  if (update.projectBoardAutomaticAssignees) {
    // The challenge holds the assignees' profiles, the update only their ids.
    next.projectBoardAutomaticAssignees =
      update.projectBoardAutomaticAssignees.map(
        (id) =>
          challenge.projectBoardAutomaticAssignees?.find(
            (assignee) =>
              (typeof assignee === "string"
                ? assignee
                : (assignee as { _id: string })._id) === id,
          ) ?? id,
      ) as ChallengeDetail["projectBoardAutomaticAssignees"];
  }
  return next;
};

export function useChallengeQuery(
  options: UseChallengeQueryOptions = {},
): UseChallengeQueryReturn {
  const params = useParams<{ id: string }>();
  const challengeId = options.challengeId ?? params.id;
  const user = useCurrentUser();

  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [challengeId ?? ""], {
      isEnabled: !!challengeId,
      refetchInterval: (query) =>
        query.state.data?.assessmentScoreProcessing ? 60000 : false,
    }),
  );
  const challenge = challengeQuery.data;

  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );

  const updateChallenge = useCallback(
    (
      update: ChallengeSettingsUpdate,
      onSuccess?: (updated: ChallengeDetail) => void,
      onError?: (err: { message: string }) => void,
    ) => {
      if (!challengeId) return;
      api.setQueryData(api.challenges.get, [challengeId], (prev) =>
        prev ? applyUpdate(prev, update) : prev,
      );
      pendingSaves.set(challengeId, (pendingSaves.get(challengeId) ?? 0) + 1);
      const settle = () => {
        const pending = (pendingSaves.get(challengeId) ?? 1) - 1;
        if (pending > 0) pendingSaves.set(challengeId, pending);
        else pendingSaves.delete(challengeId);
        return pending;
      };
      const queued = (saveQueues.get(challengeId) ?? Promise.resolve()).then(
        () =>
          new Promise<void>((resolve) => {
            api.challenges.update(
              challengeId,
              update,
              (updatedChallenge) => {
                if (settle() === 0) {
                  api.setQueryData(
                    api.challenges.get,
                    [challengeId],
                    updatedChallenge,
                  );
                }
                onSuccess?.(updatedChallenge);
                resolve();
              },
              (err) => {
                settle();
                api.invalidateQueries(api.challenges.get, [challengeId]);
                if (onError) {
                  onError(err);
                } else {
                  toast.error(err.message);
                }
                resolve();
              },
            );
          }),
      );
      saveQueues.set(challengeId, queued);
      void queued.then(() => {
        if (saveQueues.get(challengeId) === queued) {
          saveQueues.delete(challengeId);
        }
      });
    },
    [challengeId],
  );

  const updateChallengeField = useCallback(
    <K extends keyof ChallengeSettingsUpdate>(
      key: K,
      value: ChallengeSettingsUpdate[K],
    ) => {
      updateChallenge({ [key]: value } as ChallengeSettingsUpdate);
    },
    [updateChallenge],
  );

  return {
    challengeId,
    challenge,
    challengeQuery,
    user,
    canManageChallenge,
    updateChallenge,
    updateChallengeField,
  };
}
